import { combineReducers } from 'redux'
import { configureStore } from '@reduxjs/toolkit'
import { createMigrate, persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import * as reducers from './ducks'
import { migrations } from './migrations'
import api from '../utils/api'
import {
  TypedUseSelectorHook,
  // eslint-disable-next-line no-restricted-imports
  useSelector,
  // eslint-disable-next-line no-restricted-imports
  useDispatch
} from 'react-redux'
import { listenerMiddleware } from './listenerMiddleware'
import { DefaultRootState } from 'react-redux'
import { PersistConfig } from 'redux-persist'
import getInteractionModelUuidFromLocation from 'utils/getInteractionModelUuidFromLocation'
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2'

const persistConfig: PersistConfig<DefaultRootState> = {
  key: 'root',
  storage,
  version: 0,
  blacklist: ['api', 'loading'],
  migrate: createMigrate(migrations, { debug: false }),
  stateReconciler: (inboundState, originalState, reducedState, config) => {
    if (!inboundState) return reducedState

    const persistedUuid = inboundState.interactionModel?.flatData?.uuid
    const urlUuid = getInteractionModelUuidFromLocation()

    if (urlUuid && persistedUuid && urlUuid !== persistedUuid) {
      return reducedState
    }

    if (urlUuid && !persistedUuid) {
      return reducedState
    }

    return autoMergeLevel2(inboundState, originalState, reducedState, config)
  }
}

const initialState = {}

const appReducer = combineReducers(reducers)
const persistedReducer = persistReducer(persistConfig, appReducer)

const store = configureStore({
  reducer: persistedReducer,
  preloadedState: initialState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: {
        extraArgument: { api }
      },
      serializableCheck: false,
      immutableCheck: false
    }).prepend(listenerMiddleware.middleware),
  devTools: process.env.NODE_ENV !== 'production'
})

export const persistor = persistStore(store)

export default store

export type AppStore = typeof store

export type ReduxState = ReturnType<AppStore['getState']>

export type AppDispatch = AppStore['dispatch']

export const useAppSelector: TypedUseSelectorHook<DefaultRootState> =
  useSelector

export const useAppDispatch = () => useDispatch<AppDispatch>()
